import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faArrowLeft, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext.js';
import { guardWrite } from '../utils/permissions.js';
import BotUsageFooter from './BotUsageFooter.js';
import OrgSwitcher from './OrgSwitcher.js';
import BotSelector from './BotSelector.js';
import TestAgentButton from './TestAgentButton.js';
import { prefetchStudio } from './agentStudio/studioData';
import './AppSidebar.css';

// Fixed row height of a nested item — must match `.app-nav-subitem` in the
// CSS so the sliding active indicator (background pill + left line segment)
// lands exactly on the selected row via `translateY(index * ROW)`.
const SUBITEM_HEIGHT = 36;

// How long the outgoing option set fades out before the incoming one is
// mounted and cascades in. Must match `app-nav-view-out` in the CSS.
const VIEW_EXIT_MS = 170;

// Custom sidebar — replaces the old MUI Drawer/List/Collapse implementation.
//
// `pages` is the NAV model: top-level entries are either leaves (a `path`,
// no children) or expandable groups (`nestedItems`). Groups only toggle
// open; leaves navigate. `selectedIndex` ({ mainIndex, nestedIndex })
// drives the highlight and is kept in sync with the URL by App.js.
function AppSidebar({ pages, leadsCount = 0, selectedIndex, setSelectedIndex, t, visible, showBackToBots = false, isMobile = false, showWorkspaceSwitcher = true }) {
  const navigate = useNavigate();
  const { requestUnsavedAction } = useUnsavedChanges();
  const [openGroups, setOpenGroups] = useState({});

  // "+ New Agent" — shown only on the account-level nav (Agents / Workspaces /
  // Team members), where `showBackToBots` is false. Mirrors the add button on
  // the /my-bots page: guard read-only users, then open the guided
  // agent-creation flow at /agents/new.
  const handleNewAgent = () => {
    if (!guardWrite()) return;
    requestUnsavedAction(() => navigate('/agents/new'));
  };

  // ── Animated option-set swap ───────────────────────────────────────────
  // The sidebar shows two different option sets: a single "Agents" entry on
  // /my-bots, and the full agent nav once an agent is selected. We don't swap
  // them instantly — the outgoing set fades away first (`exiting`), then the
  // incoming set is mounted under a fresh key so its rows cascade in one by
  // one (CSS stagger). `rendered` is the set currently on screen; it lags the
  // `showBackToBots`/`pages` props by one fade-out.
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const [rendered, setRendered] = useState({ back: showBackToBots, pages });
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (showBackToBots === rendered.back) {
      // Settled (or an interrupted switch that returned to the current set):
      // make sure we're not stuck mid-fade.
      setExiting(false);
      return;
    }
    setExiting(true);
    const id = setTimeout(() => {
      setRendered({ back: showBackToBots, pages: pagesRef.current });
      setExiting(false);
    }, VIEW_EXIT_MS);
    return () => clearTimeout(id);
  }, [showBackToBots, rendered.back]);

  // Auto-open the group that owns the active route (without collapsing the
  // ones the user opened manually).
  useEffect(() => {
    const main = selectedIndex.mainIndex;
    if (main >= 0) {
      setOpenGroups((g) => (g[main] ? g : { ...g, [main]: true }));
    }
  }, [selectedIndex.mainIndex]);

  const go = (mainIndex, nestedIndex, path) => {
    requestUnsavedAction(() => {
      setSelectedIndex({ mainIndex, nestedIndex });
      if (path) navigate(path);
    });
  };

  // pointing at "AI Studio" loads what it shows first, so it opens with its data
  const warm = (path) => (String(path || '').startsWith('/agent-studio')
    ? () => prefetchStudio(localStorage.getItem('selectedBotId'))
    : undefined);

  const toggle = (index) =>
    setOpenGroups((g) => ({ ...g, [index]: !g[index] }));

  // Render the live `pages` prop when settled so label changes (e.g. switching
  // language in Account settings) show immediately. `rendered.pages` is only
  // the frozen snapshot used to keep the OUTGOING set on screen during the
  // option-set swap fade-out — using it when settled froze the labels at mount.
  const viewPages = exiting ? rendered.pages : pages;

  return (
    <aside className={`app-sidebar${visible ? ' app-sidebar--open' : ''}`}>
      {/* On mobile the workspace/bot selectors and the Test Agent button live
          here at the top of the sidebar (they're hidden in the header). The bot
          selector + test agent are per-agent, so they only show when an agent is
          in context (showBackToBots), matching the header's logic. */}
      {isMobile && showWorkspaceSwitcher && (
        <div className="app-sidebar-mobile-top">
          <OrgSwitcher />
          {showBackToBots && <BotSelector />}
          {showBackToBots && <TestAgentButton variant="sidebar" />}
        </div>
      )}
      <nav className="app-sidebar-nav">
        {/* Two option sets share this region: a single "Agents" entry on
            /my-bots and the full agent nav once an agent is selected. While
            `exiting`, the outgoing set fades out as a block; once it swaps,
            the new key remounts the wrapper so each row inside cascades in one
            by one via its `--nav-stagger` index. */}
        <div
          key={rendered.back ? 'agent-nav' : 'all-agents-nav'}
          className={`app-nav-view${exiting ? ' is-exiting' : ''}`}
        >
        {rendered.back && (
          <button
            type="button"
            className="app-nav-back app-nav-cascade"
            style={{ '--nav-stagger': 0 }}
            onClick={() => requestUnsavedAction(() => navigate('/my-bots'))}
          >
            <FontAwesomeIcon className="app-nav-back-icon" icon={faArrowLeft} />
            <span className="app-nav-label">
              {(t && t('all_agents')) || 'All Agents'}
            </span>
          </button>
        )}
        {viewPages.map((page, index) => {
          const hasChildren = page.nestedItems && page.nestedItems.length;
          // Position in the cascade — the back button, when shown, takes 0.
          const stagger = rendered.back ? index + 1 : index;

          if (!hasChildren) {
            const selected =
              selectedIndex.mainIndex === index &&
              selectedIndex.nestedIndex === null;
            return (
              <button
                key={page.path || index}
                type="button"
                className={`app-nav-item app-nav-cascade${selected ? ' selected' : ''}`}
                style={{ '--nav-stagger': stagger }}
                onClick={() => go(index, null, page.path)}
                onMouseEnter={warm(page.path)}
                onFocus={warm(page.path)}
              >
                {page.icon && (
                  <FontAwesomeIcon className="app-nav-icon" icon={page.icon} />
                )}
                <span className="app-nav-label">{page.label}</span>
              </button>
            );
          }

          const isOpen = !!openGroups[index];
          // Which nested item (if any) is active inside THIS group. Drives
          // the single sliding indicator (it animates between rows; when the
          // active item lives in another group it simply isn't rendered here).
          const activeNested =
            selectedIndex.mainIndex === index ? selectedIndex.nestedIndex : null;
          const indicatorStyle = {
            transform: `translateY(${(activeNested || 0) * SUBITEM_HEIGHT}px)`,
          };
          // Surface the unread-leads badge on the group that owns the Leads
          // leaf (Activity), so it shows even while the group is collapsed.
          const groupLeadCount = page.nestedItems.some((l) => l.key === 'leads')
            ? leadsCount
            : 0;
          return (
            <div key={page.label} className="app-nav-group app-nav-cascade" style={{ '--nav-stagger': stagger }}>
              <button
                type="button"
                className={`app-nav-item app-nav-parent${isOpen ? ' open' : ''}${!isOpen && activeNested != null ? ' has-active-child' : ''}`}
                onClick={() => toggle(index)}
                aria-expanded={isOpen}
              >
                {page.icon && (
                  <FontAwesomeIcon className="app-nav-icon" icon={page.icon} />
                )}
                <span className="app-nav-label">{page.label}</span>
                {groupLeadCount > 0 && (
                  <span className="app-nav-badge">{groupLeadCount}</span>
                )}
                <FontAwesomeIcon
                  className={`app-nav-chev${isOpen ? ' open' : ''}`}
                  icon={faChevronDown}
                />
              </button>

              <div className={`app-nav-subwrap${isOpen ? ' open' : ''}`}>
                <div className="app-nav-sub">
                  {/* Decorations render only while the group is open, so the
                      absolutely-positioned guide line / pill / segment can
                      never bleed out of a collapsed group. */}
                  {isOpen && <span className="app-nav-guide" />}
                  {isOpen && activeNested != null && (
                    <>
                      <span className="app-nav-active-bg" style={indicatorStyle} />
                      <span className="app-nav-active-line" style={indicatorStyle} />
                    </>
                  )}
                  {page.nestedItems.map((leaf, nestedIndex) => {
                    const selected = activeNested === nestedIndex;
                    return (
                      <button
                        key={leaf.path || nestedIndex}
                        type="button"
                        className={`app-nav-subitem${selected ? ' selected' : ''}`}
                        onClick={() => go(index, nestedIndex, leaf.path)}
                      >
                        {leaf.customIconElement ? (
                          <span className="app-nav-subicon">
                            {leaf.customIconElement}
                          </span>
                        ) : leaf.icon ? (
                          <FontAwesomeIcon
                            className="app-nav-subicon"
                            icon={leaf.icon}
                          />
                        ) : (
                          <span className="app-nav-subicon" />
                        )}
                        <span className="app-nav-label">{leaf.label}</span>
                        {leaf.key === 'leads' && leadsCount > 0 && (
                          <span className="app-nav-badge">{leadsCount}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
        </div>
      </nav>

      <div className="app-sidebar-bottom">
        {/* Usage / billing is per-agent, so it only belongs in an agent's
            context — not on the agents overview (/my-bots), where no single
            agent is in focus. `showBackToBots` is false only there. */}
        {showBackToBots && <BotUsageFooter />}
        {/* On the account-level nav (Agents / Workspaces / Team members) there's
            no per-agent footer, so surface the create-agent action here instead
            — same behaviour as the button on the /my-bots page. */}
        {!showBackToBots && (
          <button
            type="button"
            className="app-sidebar-new-agent"
            onClick={handleNewAgent}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>{(t && t('add_bot')) || 'New Agent'}</span>
          </button>
        )}
        <div className="app-sidebar-footer-links">
          <a
            href="https://ultimo-bots.com/terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            {(t && t('footer_terms')) || 'Terms'}
          </a>
          <a
            href="https://ultimo-bots.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            {(t && t('footer_privacy')) || 'Privacy'}
          </a>
          <a
            href="/help"
            onClick={(e) => {
              e.preventDefault();
              requestUnsavedAction(() => navigate('/help'));
            }}
          >
            {(t && t('help')) || 'Help'}
          </a>
        </div>
      </div>
    </aside>
  );
}

export default AppSidebar;
